import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Dumbbell,
  Users,
  UserCog,
  Store,
  Ticket,
  CreditCard,
  Palette,
  Settings,
  Library,
  ClipboardList,
  Copy,
  Package,
  MessageSquare,
  TrendingUp,
  DownloadCloud,
  UserCircle,
  CalendarDays,
  Star,
  Weight,
  Receipt,
  ScrollText,
  Moon,
  Sun,
  LogOut,
} from 'lucide-react';
import useAuthStore from '@/store/authStore';
import { useTheme } from '@/contexts/ThemeContext';
import Logo from '@/components/ui/Logo';
import Avatar from '@/components/ui/Avatar';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import NotificationsBell from './NotificationsBell';
import styles from './Sidebar.module.css';

// Navegação por papel. `to` é relativo ao prefixo do papel (montado no router).
const NAV = {
  admin: [
    { to: '/admin', key: 'dashboard', icon: LayoutDashboard, end: true },
    { to: '/admin/trainers', key: 'trainers', icon: Users },
    { to: '/admin/users', key: 'users', icon: Users },
    { to: '/admin/exercises', key: 'exercises', icon: Dumbbell },
    { to: '/admin/exercises/import', key: 'importExercises', icon: DownloadCloud },
    // Equipamentos: oculto até o catálogo ser ligado aos exercícios (rotas seguem ativas).
    // { to: '/admin/equipments', key: 'equipments', icon: Weight },
    { to: '/admin/marketplace', key: 'marketplace', icon: Store },
    { to: '/admin/reviews', key: 'reviews', icon: Star },
    { to: '/admin/coupons', key: 'coupons', icon: Ticket },
    { to: '/admin/finance', key: 'finance', icon: CreditCard },
    { to: '/admin/invoices', key: 'invoices', icon: Receipt },
    { to: '/admin/audit', key: 'audit', icon: ScrollText },
    { to: '/admin/themes', key: 'themes', icon: Palette },
  ],
  trainer: [
    { to: '/trainer', key: 'dashboard', icon: LayoutDashboard, end: true },
    { to: '/trainer/students', key: 'students', icon: Users },
    { to: '/trainer/agenda', key: 'agenda', icon: CalendarDays },
    { to: '/trainer/library', key: 'library', icon: Library },
    // Equipamentos: oculto até o catálogo ser ligado aos exercícios (rotas seguem ativas).
    // { to: '/trainer/equipments', key: 'equipments', icon: Weight },
    { to: '/trainer/workouts', key: 'workouts', icon: ClipboardList },
    { to: '/trainer/templates', key: 'templates', icon: Copy },
    { to: '/trainer/packages', key: 'packages', icon: Package },
    { to: '/trainer/chat', key: 'chat', icon: MessageSquare },
    { to: '/trainer/profile', key: 'profile', icon: UserCog },
    { to: '/trainer/evolution', key: 'evolution', icon: TrendingUp },
    { to: '/trainer/invoices', key: 'invoices', icon: Receipt },
  ],
};

const ROLE_LABEL = {
  admin: { name: 'Admin', kicker: 'PLATAFORMA' },
  trainer: { name: 'Personal', kicker: 'STUDIO' },
};

export default function Sidebar({ role }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { mode, toggleMode } = useTheme();
  const items = NAV[role] || [];
  const meta = ROLE_LABEL[role] || ROLE_LABEL.admin;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Fecha o menu do usuário ao clicar fora.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.brand}>
        <Logo size={26} />
        <div className={styles.kicker}>{role === 'admin' ? 'ADMIN' : 'STUDIO'}</div>
      </div>
      <nav className={styles.nav}>
        {items.map(({ to, key, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => clsxActive(styles.item, isActive && styles.active)}
          >
            <Icon size={16} strokeWidth={2} />
            <span>{t(`nav.${key}`)}</span>
          </NavLink>
        ))}
      </nav>
      <div className={styles.footer} ref={menuRef}>
        <button
          type="button"
          className={styles.userBtn}
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <Avatar name={user?.name || meta.name} src={user?.avatar} size={34} />
          <div className={styles.footerText}>
            <div className={styles.footerName}>{user?.name || meta.name}</div>
            <div className={styles.footerKicker}>{meta.kicker}</div>
          </div>
        </button>
        <NotificationsBell />

        {menuOpen && (
          <div className={styles.userMenu} role="menu">
            <button type="button" className={styles.menuItem} onClick={() => go(`/${role}/account`)}>
              <UserCircle size={16} strokeWidth={2} /> {t('nav.account')}
            </button>
            <button type="button" className={styles.menuItem} onClick={() => go(`/${role}/settings`)}>
              <Settings size={16} strokeWidth={2} /> {t('nav.settings')}
            </button>
            <div className={styles.menuDivider} />
            <button type="button" className={styles.menuItem} onClick={toggleMode}>
              {mode === 'dark' ? <Sun size={16} strokeWidth={2} /> : <Moon size={16} strokeWidth={2} />}
              {t('common.theme')}: {mode === 'dark' ? t('common.light') : t('common.dark')}
            </button>
            <div className={styles.menuLang}>
              <span className={styles.menuLabel}>{t('common.language')}</span>
              <LanguageSwitcher />
            </div>
            <div className={styles.menuDivider} />
            <button type="button" className={`${styles.menuItem} ${styles.menuDanger}`} onClick={handleLogout}>
              <LogOut size={16} strokeWidth={2} /> {t('common.logout')}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function clsxActive(...classes) {
  return classes.filter(Boolean).join(' ');
}
